/**
 * Typed, SSR-safe, cross-tab-synced local storage.
 *
 * Backed by `useSyncExternalStore`, so two components reading the same key can
 * never disagree, and a change in another tab lands here without a refresh.
 * Parsed values are memoised per raw string: `getSnapshot` must be referentially
 * stable or React re-renders forever.
 */

import { useCallback, useDebugValue, useMemo, useSyncExternalStore } from 'react';

/** Same-tab change notification — the `storage` event only fires cross-tab. */
const LOCAL_EVENT = 'af:localstorage';

interface CacheEntry {
  raw: string | null;
  parsed: unknown;
}

const cache = new Map<string, CacheEntry>();
const listeners = new Map<string, Set<() => void>>();
let domListenersAttached = false;

function notify(key: string): void {
  for (const listener of listeners.get(key) ?? []) listener();
}

function attachDomListeners(): void {
  if (domListenersAttached || typeof window === 'undefined') return;
  domListenersAttached = true;

  window.addEventListener('storage', (event: StorageEvent) => {
    if (event.storageArea !== window.localStorage) return;
    if (event.key === null) {
      cache.clear();
      for (const key of listeners.keys()) notify(key);
      return;
    }
    cache.delete(event.key);
    notify(event.key);
  });

  window.addEventListener(LOCAL_EVENT, (event: Event) => {
    const key = (event as CustomEvent<{ key: string }>).detail?.key;
    if (typeof key !== 'string') return;
    cache.delete(key);
    notify(key);
  });
}

function subscribe(key: string, listener: () => void): () => void {
  attachDomListeners();
  let set = listeners.get(key);
  if (!set) {
    set = new Set();
    listeners.set(key, set);
  }
  set.add(listener);
  return () => {
    set.delete(listener);
    if (set.size === 0) listeners.delete(key);
  };
}

function readRaw(key: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function readParsed<T>(key: string, deserialise: (raw: string) => T): unknown {
  const raw = readRaw(key);
  const entry = cache.get(key);
  if (entry && entry.raw === raw) return entry.parsed;

  let parsed: unknown;
  if (raw === null) {
    parsed = undefined;
  } else {
    try {
      parsed = deserialise(raw);
    } catch {
      parsed = undefined;
    }
  }
  cache.set(key, { raw, parsed });
  return parsed;
}

/** Imperative read — for module code and event handlers outside React. */
export function readLocalStorage<T>(key: string, fallback: T): T {
  const parsed = readParsed<T>(key, (raw) => JSON.parse(raw) as T);
  return parsed === undefined ? fallback : (parsed as T);
}

/** Imperative write that also wakes every hook bound to `key`, in every tab. */
export function writeLocalStorage(key: string, value: unknown): void {
  if (typeof window === 'undefined') return;
  try {
    if (value === undefined) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* Quota or private mode — state still lives in memory for this session. */
  }
  cache.delete(key);
  window.dispatchEvent(new CustomEvent(LOCAL_EVENT, { detail: { key } }));
}

export interface LocalStorageOptions<T> {
  serialise?: (value: T) => string;
  deserialise?: (raw: string) => T;
}

export type SetLocalStorage<T> = (value: T | ((previous: T) => T)) => void;

/**
 * ```ts
 * const [density, setDensity, reset] = useLocalStorage<Density>('af.inbox.density', 'comfortable');
 * ```
 * `reset` removes the key, so the next read falls back to `initialValue`.
 */
export function useLocalStorage<T>(
  key: string,
  initialValue: T,
  options: LocalStorageOptions<T> = {},
): [T, SetLocalStorage<T>, () => void] {
  const { serialise = JSON.stringify, deserialise } = options;

  const parse = useMemo<(raw: string) => T>(
    () => deserialise ?? ((raw: string) => JSON.parse(raw) as T),
    [deserialise],
  );

  const getSnapshot = useCallback(() => readParsed<T>(key, parse), [key, parse]);
  const stored = useSyncExternalStore(
    useCallback((listener: () => void) => subscribe(key, listener), [key]),
    getSnapshot,
    () => undefined, // server render: always the initial value
  );

  const value = stored === undefined ? initialValue : (stored as T);

  const setValue = useCallback<SetLocalStorage<T>>(
    (next) => {
      const current = readParsed<T>(key, parse);
      const resolved =
        typeof next === 'function'
          ? (next as (previous: T) => T)(current === undefined ? initialValue : (current as T))
          : next;

      if (typeof window !== 'undefined') {
        try {
          window.localStorage.setItem(key, serialise(resolved));
        } catch {
          /* ignore */
        }
      }
      cache.delete(key);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(LOCAL_EVENT, { detail: { key } }));
      }
    },
    [key, parse, serialise, initialValue],
  );

  const remove = useCallback(() => {
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.removeItem(key);
      } catch {
        /* ignore */
      }
      cache.delete(key);
      window.dispatchEvent(new CustomEvent(LOCAL_EVENT, { detail: { key } }));
    }
  }, [key]);

  useDebugValue(value);
  return [value, setValue, remove];
}
